<template>
  <div
    v-if="lastScreenshots.length"
    class="tracking-info pointer-events-none fixed top-12 right-0 bottom-0 z-[calc(var(--z-nav)+1)] flex w-64
           transition-transform duration-300"
    :class="showWindow ? 'translate-x-0' : 'translate-x-full'"
  >
    <button
      class="pointer-events-auto absolute -left-[26px] top-1/2 flex h-[64px] w-[26px] -translate-y-1/2 cursor-pointer
             items-center justify-center rounded-l-card border-0 bg-accent text-on-accent
             transition-colors hover:bg-accent-hover"
      :title="$t('task.my_last_screenshots')"
      @click="changeVisibleWindow(!showWindow)"
    >
      <span
        class="size-0 border-x-[0.625rem] border-x-transparent border-b-[0.625rem] border-b-current
               transition-transform duration-300"
        :class="showWindow ? 'rotate-90' : '-rotate-90'"
      ></span>
    </button>

    <aside
      class="pointer-events-auto flex max-h-full w-full flex-col self-center rounded-l-card border border-r-0 border-line
             bg-chrome [backdrop-filter:var(--glass-blur)]"
    >
      <div class="flex shrink-0 items-center gap-2 px-4 pb-3 pt-4">
        <IconCamera16Solid class="size-4 text-accent" />
        <span class="text-body font-bold text-text">{{ $t('task.my_last_screenshots') }}</span>
      </div>

      <div class="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4">
        <div
          v-for="s in lastScreenshots"
          :key="s.id"
          class="screenshot-card group relative aspect-[16/10] shrink-0 overflow-hidden rounded-[0.625rem]
                 border border-line transition-colors hover:border-accent"
        >
          <Screenshot
            :screenshot="s"
            :user="user"
            @delete="actionDeleteScreenshot"
          />
          <ConfirmButton
            class="absolute! bottom-2 left-2 invisible group-hover:visible"
            :popupTitle="$t('task.delete_screenshot_title')"
            :popupSubTitle="$t('task.delete_screenshot_subtitle')"
            @confirm="actionDeleteScreenshot(s)"
          >
            <template #menu-trigger>
              <SimpleButton
                icon="delete-task"
                class="text-text-muted! hover:text-accent!"
              />
            </template>
          </ConfirmButton>
        </div>
      </div>

      <div class="shrink-0 px-4 pt-3 pb-4">
        <SimpleButton size="md" class="w-full" @click="openMyScreenshots">
          {{ $t('task.view_all') }}
        </SimpleButton>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import ConfirmButton from '@/components/elements/ConfirmButton';
import { IconCamera16Solid } from '@iconify-prerendered/vue-heroicons';

const NAME_HIDE_WINDOW = 'showWindowScreenshots';

const store = useStore();
const router = useRouter();

const showWindow = ref(window.APP_TRON); // on app there is no big need in this window so let's hide by default

const lastScreenshots = computed(() => store.state.user.lastScreenshots);
const user = computed(() => store.state.user.user);
const taskUnderEdit = computed(() => store.state.project.taskUnderEdit);

const actionDeleteScreenshot = (screenshot) => store.dispatch('project/actionDeleteScreenshot', screenshot);

const changeVisibleWindow = (status) => {
  showWindow.value = status;
  localStorage.setItem(NAME_HIDE_WINDOW, +status);
};

const openMyScreenshots = () => {
  router.push({
    name: 'tasks-time-spent',
    query: { mode: 'screenshots', executors: user.value.id },
  });
  changeVisibleWindow(true);
};

onMounted(async () => {
  await store.dispatch('user/getLastScreenshots');
  showWindow.value = +localStorage.getItem(NAME_HIDE_WINDOW);
});
</script>
<style lang="scss" scoped>
.tracking-info {
  z-index: calc(var(--z-nav) + 2);
}

.screenshot-card {
  :deep(.screenshot-wrap),
  :deep(.img-container) {
    position: absolute;
    inset: 0;
    min-height: 0;
    max-height: none;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }
  :deep(.screenshot) {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    object-fit: cover;
  }
  :deep(.avatar),
  :deep(.screen__info-item),
  :deep(.screen-browser),
  :deep(.tooltip-activity),
  :deep(.delete) {
    display: none;
  }
  :deep(.stats) {
    position: absolute;
    top: 8px;
    right: 8px;
    margin: 0;
  }
  :deep(.time) {
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.55);
    font-size: var(--fs-label);
    font-weight: 700;
  }
  :deep(.time),
  :deep(.time *) {
    color: #fff !important;
  }
  :deep(.confirm-button-wrapper .button-wrapper) {
    min-width: 2rem;
  }
}
</style>
